// V1-Starter-Picker: UC-Tabs + Method-Tiles oben, Filter-Sidebar links,
// Board-Liste rechts. Look orientiert sich am Prototyp, ist aber frisch komponiert.

import {
  METHOD_CATALOG,
  type MethodId,
  USE_CASES,
  type UseCaseId,
  methodsForUseCase,
} from "@ms-whiteboard/shared";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext.js";
import { BoardCard } from "./BoardCard.js";
import { useBoards } from "./use-boards.js";
import "./StarterPicker.css";

type FilterKey = "my" | "recent" | "shared" | "archived";

const FILTERS: readonly { key: FilterKey; status: "active" | "archived" }[] = [
  { key: "my", status: "active" },
  { key: "recent", status: "active" },
  { key: "shared", status: "active" },
  { key: "archived", status: "archived" },
];

interface StarterPickerProps {
  onOpenBoard: (boardId: string) => void;
}

export function StarterPicker({ onOpenBoard }: StarterPickerProps): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [activeUc, setActiveUc] = useState<UseCaseId>("uc-01-ist");
  const [filter, setFilter] = useState<FilterKey>("my");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const filterConfig = FILTERS.find((f) => f.key === filter) ?? FILTERS[0];
  const nextStatus = filterConfig?.status ?? "active";
  const {
    boards,
    loading,
    error,
    status,
    setStatus,
    createBoard,
    renameBoard,
    archiveBoard,
    refresh,
  } = useBoards(nextStatus);

  // Filter-Wechsel ("Archiviert") muss den Backend-Status mitziehen — useBoards
  // packt den Initial-Wert nur in useState, ignoriert spaetere Aenderungen.
  useEffect(() => {
    if (status !== nextStatus) setStatus(nextStatus);
  }, [nextStatus, status, setStatus]);

  const filtered = useMemo(() => {
    if (!user) return [];
    if (filter === "my") return boards.filter((b) => b.ownerId === user.id);
    if (filter === "shared") return boards.filter((b) => b.ownerId !== user.id);
    return boards;
  }, [boards, filter, user]);

  const ucMethods = useMemo(() => methodsForUseCase(activeUc), [activeUc]);

  const createWithMethod = async (methodId: MethodId | null): Promise<void> => {
    if (creating) return;
    setCreating(true);
    setCreateError(null);
    try {
      const method = METHOD_CATALOG.find((m) => m.id === methodId);
      const defaultName = method ? t(method.nameKey) : t("starter.create.blankName");
      const board = await createBoard({ name: defaultName, methodTemplate: methodId });
      onOpenBoard(board.id);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "create failed");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="starter">
      <section className="starter-hero">
        <h2 className="starter-hero-title">{t("starter.hero.title")}</h2>
        <p className="starter-hero-sub">{t("starter.hero.subtitle")}</p>
        <div className="starter-uc-tabs" role="tablist">
          {USE_CASES.map((uc) => (
            <button
              key={uc.id}
              type="button"
              role="tab"
              aria-selected={activeUc === uc.id}
              className={`starter-uc-tab ${activeUc === uc.id ? "is-active" : ""}`}
              onClick={() => setActiveUc(uc.id)}
            >
              <span className="starter-uc-code">{uc.shortCode}</span>
              <span className="starter-uc-name">{t(uc.nameKey)}</span>
            </button>
          ))}
        </div>
        <div className="starter-methods" role="tabpanel">
          <button
            type="button"
            className="starter-method-tile starter-method-blank"
            onClick={() => createWithMethod(null)}
            disabled={creating}
          >
            <span className="starter-method-name">{t("starter.create.blank")}</span>
            <span className="starter-method-desc">{t("starter.create.blankHint")}</span>
          </button>
          {ucMethods.map((m) => (
            <button
              key={m.id}
              type="button"
              className={`starter-method-tile starter-method-family-${m.family}`}
              onClick={() => createWithMethod(m.id)}
              disabled={creating}
            >
              <span className="starter-method-name">{t(m.nameKey)}</span>
              <span className="starter-method-desc">{t(m.descriptionKey)}</span>
            </button>
          ))}
        </div>
        {createError && <p className="starter-error">{createError}</p>}
      </section>

      <section className="starter-library">
        <aside className="starter-filter">
          <h3>{t("starter.filter.title")}</h3>
          <nav>
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`starter-filter-item ${filter === f.key ? "is-active" : ""}`}
                onClick={() => setFilter(f.key)}
              >
                {t(`starter.filter.${f.key}`)}
              </button>
            ))}
          </nav>
          <button type="button" className="starter-refresh" onClick={() => void refresh()}>
            ↻ {t("starter.refresh")}
          </button>
        </aside>
        <div className="starter-list">
          {loading && <p className="starter-muted">{t("starter.list.loading")}</p>}
          {error && <p className="starter-error">{error}</p>}
          {!loading && !error && filtered.length === 0 && (
            <p className="starter-muted">{t("starter.list.empty")}</p>
          )}
          <div className="starter-grid">
            {filtered.map((b) => (
              <BoardCard
                key={b.id}
                board={b}
                isOwner={b.ownerId === user?.id}
                onOpen={onOpenBoard}
                onRename={renameBoard}
                onArchive={archiveBoard}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
